<template>
    <div>
        <v-menu :close-on-content-click="false" offset-x open-on-hover :close-delay="closingDelay">
            <template v-slot:activator="{ on }">
                <v-list-item
                    v-on="on"
                    :to="{
                        name: 'app-open',
                        params: {
                            oid: $route.params.oid,
                            sid: $route.params.sid,
                            iid: $route.params.iid,
                            snid: $route.params.snid,
                            aid: appData.aid,
                            sessionId: appData.session_id
                        },
                        query: $route.query
                    }">
                    <v-list-item-content class="text-center">
                        <div style="position: relative" v-if="appData.app_type !== undefined">
                            <div
                                style="position: absolute; top: 0; right: 0; z-index: 1000"
                                v-if="appData.mount_handback_to_files_for_hid !== 'None' || appData.mount_handback_for_bid !== 'None'">
                                <div class="mt-n4 mr-n4" style="background-color: var(--v-primary-base)">
                                    <v-icon dark v-if="appData.mount_handback_to_files_for_hid !== 'None'">grading</v-icon>
                                    <v-icon dark v-if="appData.mount_handback_for_bid !== 'None'">visibility</v-icon>
                                </div>
                            </div>
                            <div :class="collapsed ? 'd-flex flex-column align-center' : 'd-flex align-center justify-center'">
                                <v-badge color="light-green" overlap v-if="appData.gpu">
                                    <template v-slot:badge>
                                        <v-icon small class="nudgeUp">mdi-memory mdi-rotate-45</v-icon>
                                    </template>
                                    <img width="35" height="35" :src="appIcon(appData)" />
                                </v-badge>
                                <img v-else width="35" height="35" :src="appIcon(appData)" />
                                <div :class="collapsed ? 'mt-2 w-100' : 'ml-2 w-100'">
                                    <TheSideBarResourceInfo :currentApp="getAppInfo(appData.aid)" :collapsed="collapsed" />
                                </div>
                            </div>
                        </div>
                    </v-list-item-content>
                </v-list-item>
            </template>

            <v-list nav dense>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-title class="text-body-1">
                            <b>{{ appData.name }}</b>
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption" v-if="appData.gpu">on {{ appData.gpu }} GPU</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="appData.shared === 'true'">
                        <v-chip small outlined>shared</v-chip>
                    </v-list-item-action>
                </v-list-item>

                <v-divider class="mb-1"></v-divider>

                <v-list-item>
                    <v-list-item-content>
                        <v-card flat color="grey lighten-4">
                            <v-card-text class="d-flex align-center">
                                <div>Memory limit:</div>
                                <v-spacer></v-spacer>
                                <div>
                                    <span class="font-weight-medium ml-1">{{ memoryLimitAbs }} GB</span>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon color="secondary" class="ml-2" dark v-on="on" small>info</v-icon>
                                        </template>
                                        <span>If this is breached, the app is restarted. There can be some delay in reporting.</span>
                                    </v-tooltip>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-list-item-content>
                </v-list-item>

                <v-divider class="mb-2"></v-divider>

                <template v-if="appData.ipc_mode !== 'none'">
                    <v-list-item>
                        <v-list-item-content>
                            <div class="d-flex align-center justify-space-between">
                                <div>Exposed in:</div>
                                <div>{{ getIpcName(appData.ipc_mode) }}</div>
                            </div>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-content>
                            <div class="d-flex align-center justify-space-between">
                                <div class="pr-4">Hostname:</div>
                                <code class="pl-3 py-1">
                                    {{ truncateMid(appData.ipc_hostname, 7) }}
                                    <copy-to-clipboard :textToCopy="appData.ipc_hostname" buttonName="copy to clipboard" :iconButton="true"></copy-to-clipboard>
                                </code>
                            </div>
                        </v-list-item-content>
                    </v-list-item>
                    <v-divider class="my-3" color="grey"></v-divider>
                </template>
                <v-list-item-group>
                    <v-list-item @click="openAppInNewTab" active-class="hide-active-state">
                        <v-list-item-icon>
                            <v-icon>launch</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Open in new tab</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item @click="$router.push({ name: 'snapshot-applications', query: { resetAid: appData.aid } })">
                        <v-list-item-icon>
                            <v-icon>auto_fix_high</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title class="mr-5">Clear user application settings</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                        v-if="$appConfig.VUE_APP_INTERCOM_ID"
                        @click="showNewMessageIntercom('Hi Nuvolos Team, \nI would like to ask your help regarding the following:\n', userInfo)">
                        <v-list-item-icon>
                            <v-icon>mdi-help-circle-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Contact support</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <stop-application
                        :app="appData"
                        :appName="appData.name"
                        :isShared="getAppInfo(appData.aid).shared === 'true'"
                        :sessionId="appData.session_id">
                        <v-list-item>
                            <v-list-item-icon>
                                <v-icon>stop</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Stop</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </stop-application>
                </v-list-item-group>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { appTypeAndImageLink } from '@/mixins/appTypeAndImage'
import { intercomMethods } from '@/mixins/intercom'
import { truncateMid, ipcOptions } from '@/utils'
const TheSideBarResourceInfo = () => import('./TheSideBarResourceInfo.vue')
const StopApplication = () => import('@/modules/application/components/TheApplicationStopDialog')
const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')

export default {
    mixins: [appTypeAndImageLink, intercomMethods],
    components: {
        TheSideBarResourceInfo,
        StopApplication,
        CopyToClipboard
    },
    props: {
        appData: Object,
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            closingDelay: 100
        }
    },
    computed: {
        ...mapGetters('appStore', ['runningDeployments', 'startingDeployments']),
        ...mapState(['userOrgs', 'userInfo']),
        memoryLimitAbs() {
            if (this.getAppInfo(this.appData.aid) && this.getAppInfo(this.appData.aid).limit_memory !== 'N/A') {
                return (parseFloat(this.getAppInfo(this.appData.aid).limit_memory.replace('M', '')) / 1000).toFixed(0)
            }
            return 0
        }
    },
    methods: {
        getAppInfo(aid) {
            return this.runningDeployments.concat(this.startingDeployments).find(a => {
                return a.aid.toString() === aid.toString()
            })
        },
        truncateMid(text, length) {
            return truncateMid(text, length)
        },
        getIpcName(value) {
            const ipc = ipcOptions.find(ipc => ipc.value === value)
            return ipc.text
        },
        openAppInNewTab() {
            console.log(this.appData)
            // Construct the iframe URL similar to AppIframe.vue's proxy_url
            if (!this.appData.domain || this.appData.available_replicas < 1) {
                return
            }

            let openedFile = ''
            if (this.appData.subpath) {
                openedFile = this.appData.subpath
            }

            let query = ''
            if (this.appData.app_type === 'jupyter') {
                const userName = this.userInfo.name || this.userInfo.given_name + ' ' + this.userInfo.family_name
                query = `username=${encodeURIComponent(userName.replace(/\s/g, ''))}`
            }

            const url = 'https://' + this.appData.domain + openedFile + (query ? `?${query}` : '')
            window.open(url, '_blank')
        }
    }
}
</script>

<style scoped>
.hide-active-state::before {
    background: transparent !important;
}

::v-deep .nudgeUp {
    margin-top: -2px;
}
</style>
